import {
  useState, useCallback, useEffect, useRef
} from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import moment from 'moment';

import {
  Box, TextField, InputAdornment
} from '@mui/material';

import { useSnackbar } from 'notistack';

import { LoadingButton } from '@mui/lab';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { Key, Person } from '@mui/icons-material';
import { skipToken } from '@reduxjs/toolkit/query/react';

import { StartPagesContainer } from '../shared/StartPagesContainer';

import { useStyles } from './style';

// eslint-disable-next-line import/named
import { useGetProfileQuery } from '../../redux/services/FindProfile/FindProfile';
import {
  profileInfoAction, insuranceListingAction, programContactsAction, apiMessageCodeAction, allowDonorPhotoUploadAction
} from '../../redux/profile/Reducer';
import { cameraImageAction } from '../../redux/camera/Reducer';

export const LoginPage = () => {
  const [dateVal, setDateVal] = useState(null);
  const [formData, setFormData] = useState(skipToken);
  const errorGetProfile = useRef(false);
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    currentData: profileResponse, isError, isSuccess, isLoading
  } = useGetProfileQuery(formData);

  const dipatchDataToReduxStore = async (res) => {
    const adminSetting = res.AdminSettings[0];
    const adminSettingsBool = adminSetting.AllowDonorPhotoUpload === 'true';
    const donorImage = await res.Data.DonorImage;
    const donorPhoto = donorImage?.includes('base64') ? donorImage : `data:image/jpeg;base64,${donorImage}`;
    await dispatch(allowDonorPhotoUploadAction(adminSettingsBool));
    await dispatch(profileInfoAction(res.Data));
    await dispatch(insuranceListingAction(res.InsuranceListing));
    await dispatch(programContactsAction(res.ProgramContacts));
    await dispatch(apiMessageCodeAction({ msg: res.Message, code: res.Code }));
    await dispatch(cameraImageAction(donorPhoto));
  };
  useEffect(() => {
    if (isSuccess) {
      if (profileResponse.Message === 'Found' && profileResponse.Code === 2000) {
        dipatchDataToReduxStore(profileResponse).then(() => {
          history.push('/welcome');
          setFormData(skipToken);
        });
      } else {
        history.push('/no-profile');
        setFormData(skipToken);
        dispatch(apiMessageCodeAction({ msg: profileResponse.Message, code: profileResponse.Code }));
      }
    }
    if (isError) {
      errorGetProfile.current = true;
      setFormData(skipToken);
    }
  }, [formData, profileResponse, isError, isSuccess, errorGetProfile.current]);

  useEffect(() => {
    if (errorGetProfile.current) {
      enqueueSnackbar('something went wrong in SignIn', { variant: 'error' });
    }

    errorGetProfile.current = false;
  }, [errorGetProfile.current]);

  const formik = useFormik({
    initialValues: {
      dob: '',
      pin: '',
      lastName: '',
    },
    validationSchema: yup.object({
      pin: yup
        .string()
        .trim()
        .matches(
          /* eslint-disable no-useless-escape */
          /^[0-9]+$/,
          'Enter a valid pin'
        )
        .required('This field is required*'),
      lastName: yup
        .string()
        .trim()
        .required('This field is required*'),
      dob: yup
        .string()
        .matches(
          /* eslint-disable no-useless-escape */
          /(0[1-9]|1[012])[ \/.](0[1-9]|[12][0-9]|3[01])[ \/.](19|20)\d\d/,
          'Enter a valid DOB'
        )
        .required('This field is required*'),
    }),
    onSubmit: (data) => {
      setFormData({
        pin: data.pin,
        lastName: data.lastName,
        birthDate: data.dob
      });
    },
  });

  const onDobChange = useCallback(
    (d) => {
      setDateVal(d);
      let fomattedDate = moment(d);
      if (fomattedDate.isValid()) {
        fomattedDate = fomattedDate.format('MM/DD/yyyy');
      } else {
        fomattedDate = '';
      }
      formik.setFieldValue('dob', fomattedDate);
    },
    [dateVal]
  );

  return (
    <StartPagesContainer addBgBlur>
      <form onSubmit={formik.handleSubmit} className={`loginLayout ${classes.loginForm}`}>
        <Box component="h1" className={classes.loginFormTitle}>
          Please fill out the form below to find your profile:
        </Box>
        <Box>
          <Box className={classes.commonInputStyle}>
            <TextField
              name="pin"
              id="pin"
              placeholder="PIN"
              variant="outlined"
              type="password"
              pattern="[0-9]*"
              inputMode="numeric"
              onChange={formik.handleChange}
              onBlur={() => formik.setFieldTouched('pin')}
              value={formik.values.pin || ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Key />
                  </InputAdornment>
                ),
              }}
            />
            {formik.touched.pin && formik.errors.pin && (
              <div className={classes.errorText}>{formik.errors.pin}</div>
            )}
          </Box>
          <Box className={classes.commonInputStyle}>
            <TextField
              name="lastName"
              id="lastName"
              placeholder="Last Name"
              variant="outlined"
              onChange={formik.handleChange}
              onBlur={() => formik.setFieldTouched('lastName')}
              value={formik.values.lastName || ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Person />
                  </InputAdornment>
                ),
              }}
            />
            {formik.touched.lastName && formik.errors.lastName && (
              <div className={classes.errorText}>{formik.errors.lastName}</div>
            )}
          </Box>
          <Box className={`${classes.commonInputStyle} ${classes.datePickerInput}`}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DesktopDatePicker
                value={dateVal || ''}
                onChange={onDobChange}
                sx={{ borderColor: 'none' }}
                renderInput={(params) => <TextField
                  {...params}
                  onBlur={() => formik.setFieldTouched('dob')}
                  inputProps={{
                    ...params.inputProps,
                    placeholder: 'Date Of Birth',
                  }}
                />}
              />
            </LocalizationProvider>
            {formik.touched.dob && formik.errors.dob && (
              <div className={classes.errorText}>{formik.errors.dob}</div>
            )}
          </Box>
          <LoadingButton
            loading={isLoading}
            type="submit"
            variant="contained"
            color="warning"
            className={classes.findProfileBtn}
            sx={{ fontSize: 25 }}
            disabled={
              !!(formik.errors.dob
                || formik.errors.cell
                || formik.errors.pin
                || !(
                  (formik.touched.pin || formik.values.pin || formik.values.pin !== '')
                  && (formik.touched.lastName || formik.values.lastName || formik.values.lastName !== '')
                  && (formik.touched.dob || formik.values.dob || formik.values.dob !== '')
                ))
            }
          >
            Find My Profile
          </LoadingButton>
        </Box>
      </form>
    </StartPagesContainer>
  );
};
